import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { mangaCategories, mangaList } from '@/mocks/manga';

export default function Category() {
  const { categoryId } = useParams<{ categoryId: string }>();
  const [activeCategory, setActiveCategory] = useState(categoryId || 'all');
  const [sortBy, setSortBy] = useState('popular');

  useEffect(() => {
    if (categoryId) {
      setActiveCategory(categoryId);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [categoryId]);

  const filteredManga = mangaList.filter((manga) => {
    if (activeCategory === 'all') return true;
    return manga.category === activeCategory;
  });

  const sortedManga = [...filteredManga].sort((a, b) => {
    if (sortBy === 'popular') return parseFloat(b.views) - parseFloat(a.views);
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'newest') return 0;
    return 0;
  });

  const currentCategoryName = mangaCategories.find((c) => c.id === activeCategory)?.name || '全部';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategoryName}漫画 - YY漫画网`,
    description: `YY漫画网${currentCategoryName}漫画分类页面，浏览${currentCategoryName}漫画排行榜和推荐，全集漫画免费下拉式阅读。`,
    url: `https://yymanhua.com/category/${activeCategory}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: 'YY漫画网',
      url: 'https://yymanhua.com',
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: '首页',
          item: 'https://yymanhua.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: currentCategoryName,
          item: `https://yymanhua.com/category/${activeCategory}`,
        },
      ],
    },
  };

  return (
    <>
      <JsonLd data={jsonLd} />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100 py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-accent-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">{currentCategoryName}</span>
            </nav>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              {currentCategoryName}漫画
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              共找到 <strong className="text-accent-500">{filteredManga.length}</strong> 部漫画
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-col lg:flex-row gap-6">
            <aside className="w-full lg:w-56 flex-shrink-0">
              <div className="sticky top-20">
                <h4 className="text-sm font-semibold text-foreground-800 mb-3">漫画分类</h4>
                <div className="flex flex-wrap lg:flex-col gap-2">
                  {mangaCategories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors whitespace-nowrap ${
                        activeCategory === cat.id
                          ? 'bg-accent-100 text-accent-600 font-medium'
                          : 'text-foreground-600 hover:bg-background-100'
                      }`}
                    >
                      <i className={`${cat.icon} w-4 h-4 flex items-center justify-center`}></i>
                      {cat.name}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>

            <div className="flex-1">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold text-foreground-900">
                  {currentCategoryName}漫画列表
                </h2>
                <div className="flex items-center gap-1 bg-background-100 rounded-full px-1 py-1">
                  {[
                    { key: 'popular', label: '最热' },
                    { key: 'rating', label: '评分' },
                    { key: 'newest', label: '最新' },
                  ].map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSortBy(s.key)}
                      className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap ${
                        sortBy === s.key
                          ? 'bg-accent-500 text-white'
                          : 'text-foreground-600 hover:text-foreground-900'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {sortedManga.map((manga) => (
                  <Link
                    key={manga.id}
                    to={`/manga/${manga.id}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className="group"
                  >
                    <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                      <img
                        src={manga.cover}
                        alt={manga.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          manga.status === '连载中'
                            ? 'bg-accent-500 text-white'
                            : 'bg-foreground-500 text-white'
                        }`}>
                          {manga.status}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <div className="flex items-center gap-1 text-white/90 text-xs">
                          <i className="ri-star-fill text-accent-400"></i>
                          <span>{manga.rating}</span>
                        </div>
                      </div>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-accent-600 transition-colors truncate">
                      {manga.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
                  </Link>
                ))}
              </div>

              {sortedManga.length === 0 && (
                <div className="text-center py-20">
                  <i className="ri-inbox-line text-4xl text-foreground-300"></i>
                  <p className="text-foreground-500 mt-3">暂无该分类的漫画</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}